import { ReactNode, memo } from "react";
import type { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import { MiChip, MiIcon } from "@miplatform/components";
import { mdiPlus } from "@mdi/js";
import type { SxProps } from "@mui/material";
import { Box, Tooltip, Typography } from "@mui/material";
import { DEFAULT_ASSIGNMENTS_COLUMN_WIDTH } from "./AssignmentsCalendar.constants";
import { AssignmentsColumnSection } from "./AssignmentsColumnSection";

interface AssignmentsColumnProps {
  isEmpty?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  isOrdersColumn?: boolean;
  title?: string;
  subtitle?: string;
  count?: number | string;
  additionalCount?: number | string;
  width?: number;
  minHeight?: number;
  sections?: {
    date: string | Moment;
    count?: number;
    children?: ReactNode;
  }[];
  children?: ReactNode;
  sx?: SxProps;
  headerSx?: SxProps;
  headerChildren?: ReactNode;
  onClick?: () => void;
  onHeaderClick?: () => void;
}

export const AssignmentsColumn = memo(
  ({
    isEmpty,
    isOrdersColumn,
    isDisabled,
    isSelected,
    title,
    subtitle,
    count,
    additionalCount,
    width = DEFAULT_ASSIGNMENTS_COLUMN_WIDTH,
    minHeight,
    sections,
    children,
    sx,
    onClick,
    onHeaderClick,
  }: AssignmentsColumnProps) => {
    const titleMaxWidth = additionalCount ? width - 100 : width - 50;
    const { theme, classes } = useStyles({
      isDisabled,
      isSelected,
      isClickEnabled: Boolean(onClick),
      minHeight,
      width,
      titleMaxWidth,
    });

    const renderSections = () =>
      sections?.map((section) => (
        <AssignmentsColumnSection
          key={section.date.toString()}
          date={section.date}
          count={section.count}
          height={`${Math.floor(100 / (sections.length || 1))}%`}
        >
          {section.children}
        </AssignmentsColumnSection>
      ));

    return (
      <Box
        className={classes.root}
        sx={{ maxWidth: width, minWidth: width, ...sx }}
        onClick={onClick}
      >
        {title && (
          <Box
            className={classes.header}
            {...(!isDisabled && {
              onClick: onHeaderClick,
              sx: {
                cursor: !isDisabled && onHeaderClick ? "pointer" : "default",
              },
            })}
          >
            <Box className={classes.row}>
              <Box display="flex" flexDirection="column">
                <Box className={classes.row}>
                  <Tooltip title={title}>
                    <Typography noWrap className={classes.title}>
                      {title}
                    </Typography>
                  </Tooltip>
                  <MiChip
                    label={count || 0}
                    size="small"
                    color="error"
                    sx={{ height: 14, fontSize: 11 }}
                  />
                  {Boolean(additionalCount) && (
                    <>
                      <MiIcon
                        path={mdiPlus}
                        color={theme.palette.info.main}
                        size={0.66}
                      />
                      <MiChip
                        label={additionalCount}
                        size="small"
                        color="info"
                        sx={{
                          height: 14,
                          fontSize: 11,
                        }}
                      />
                    </>
                  )}
                </Box>
                {subtitle && (
                  <Tooltip title={subtitle}>
                    <Typography noWrap className={classes.subtitle}>
                      {subtitle}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        )}
        {children && !isEmpty && (
          <AssignmentsColumnSection>{children}</AssignmentsColumnSection>
        )}
        {sections && renderSections()}
        {((!sections && !children) || isEmpty) && (
          <AssignmentsColumnSection isOrdersColumn={isOrdersColumn} />
        )}
      </Box>
    );
  }
);

const useStyles = makeStyles<{
  isDisabled?: boolean;
  isSelected?: boolean;
  isClickEnabled: boolean;
  minHeight?: number;
  width: number;
  titleMaxWidth: number;
}>()(
  (
    theme,
    { isDisabled, isSelected, minHeight, width, titleMaxWidth, isClickEnabled }
  ) => ({
    root: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.grey.light,
      border: `1px solid ${
        isSelected ? theme.palette.primary.main : theme.palette.grey.accent
      }`,
      height: "100%",
      minHeight: minHeight || 260,
      overflow: "hidden",
      ...(isClickEnabled && {
        cursor: "pointer",
      }),
      ...(isDisabled && {
        opacity: 0.2,
      }),
    },
    header: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    row: {
      display: "flex",
      gap: theme.spacing(1),
      alignItems: "center",
    },
    title: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.semiBold,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: titleMaxWidth,
    },
    subtitle: {
      fontSize: theme.fontSize.xsmall,
      fontWeight: theme.fontWeight.regular,
      color: theme.palette.grey.medium,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      maxWidth: width - 20,
      marginTop: -4,
    },
  })
);
